/** @format */
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import {
    Skeleton,
    SkeletonVariant,
} from '@mpb-com/feather/components/atoms/skeleton';
import { useQuery } from '@tanstack/react-query';
import { Trans, useTranslation } from 'next-i18next';
import React, {
    Fragment,
    FunctionComponent,
    ReactNode,
    useContext,
    useMemo,
} from 'react';
import styled from 'styled-components';

import { Milliseconds } from '@toucan/client/global/application';
import { MarketContext } from '@toucan/client/global/market';
import { ModelPriceModifierGroup } from '@toucan/client/global/model';

import { Link, LinkSize } from '@toucan/client/shared/components/link';

import { type FilterOption } from '@toucan/client/features/search';
import { getModelIdFromQuery } from '@toucan/client/features/search/helpers';

import { usePriceModifierGroups } from '../../hooks/use-price-modifier-groups';
import { SearchFilterMenuOptionList } from '../option-list';
import { DEFAULT_TEST_ID } from './constants';
import {
    getDefaultSelectedIncludedAccessories,
    getExtraAccessories,
    getIncludedAccessories,
} from './helpers';
import { styles } from './price-modifier-options.styles';
import { SearchFilterMenuPriceModifierOptionsProps } from './types';

const StyledSearchFilterMenuPriceModifierOptions = styled.div`
    ${styles}
`;

export const SearchFilterMenuPriceModifierOptions: FunctionComponent<
    SearchFilterMenuPriceModifierOptionsProps
> = ({
    onSelect,
    onClear,
    filter,
    isLoading = false,
    selectedOptions = [],
    selectedCount,
    'data-testid': dataTestId = DEFAULT_TEST_ID,
}) => {
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);

    const modelId = getModelIdFromQuery(filter.baseFilterQuery);

    const { data: modifierGroups, isFetching } = usePriceModifierGroups(
        modelId,
        activeMarket.code
    );

    const { data: allOptions, isFetching: isFetchingOptions } = useQuery({
        queryKey: ['price-modifier-facets', modelId],
        queryFn: filter.getAllOptions,
        select: (options) => options.map(Number),
        staleTime: Milliseconds.ONE_HOUR,
    });

    const availableOptions = filter.options.map(({ value }) => value);

    const hasIncludedAccessories = modifierGroups?.some(
        (group) => group.isEssential
    );
    const includedAccessories = useMemo(
        () => getIncludedAccessories(modifierGroups, allOptions),
        [modifierGroups, allOptions]
    );

    const extraAccessories = useMemo(
        () => getExtraAccessories(modifierGroups, allOptions),
        [modifierGroups, allOptions]
    );
    const defaultSelected = useMemo(
        () => getDefaultSelectedIncludedAccessories(includedAccessories),
        [includedAccessories]
    );

    const selectedOptionsWithDefaults = [
        ...selectedOptions,
        ...getFilterOptions(defaultSelected),
    ];

    const isLoadingData = isLoading || isFetching || isFetchingOptions;

    function isOptionDisabled(id: number) {
        return (
            !availableOptions.includes(`${id}`) ||
            defaultSelected.some((option) => option.id === id)
        );
    }

    function getFilterOptions(modifiers: ModelPriceModifierGroup['modifiers']) {
        return modifiers.map(({ id, name }) => {
            return {
                label: name,
                value: `${id}`,
                disabled: isOptionDisabled(id),
            };
        });
    }

    function renderTitle(title: ReactNode) {
        if (isLoadingData) {
            return (
                <Skeleton
                    data-testid={`${dataTestId}__title-skeleton`}
                    variant={SkeletonVariant.TEXT}
                />
            );
        }

        return title;
    }

    function renderDescription(description: ReactNode) {
        if (isLoadingData) {
            return (
                <Skeleton
                    data-testid={`${dataTestId}__description-skeleton`}
                    variant={SkeletonVariant.TEXT}
                    lineCount={2}
                />
            );
        }

        return description;
    }

    function renderIncludedAccessoriesHeader() {
        return (
            <Fragment>
                <Heading size={HeadingSize.H4}>
                    {renderTitle(t('Included accessories'))}
                </Heading>
                <div className="filter-description">
                    {renderDescription(
                        <Trans components={{ ['bold-text']: <strong /> }}>
                            {t(
                                'All items include the <bold-text>essentials</bold-text> – like a battery, cap or charger.'
                            )}
                        </Trans>
                    )}
                </div>
            </Fragment>
        );
    }

    function renderExtraAccessoriesHeader() {
        const description = extraAccessories.length
            ? t(
                  'Some items come with <bold-text>additional</bold-text> accessories. Choose any must-haves to narrow your search.'
              )
            : t(
                  "Some items include additional accessories. There's not any available for this model right now."
              );

        return (
            <Fragment>
                <Heading size={HeadingSize.H4}>
                    {renderTitle(t('Extra accessories'))}
                </Heading>
                <div className="filter-description">
                    {renderDescription(
                        <Trans components={{ ['bold-text']: <strong /> }}>
                            {description}
                        </Trans>
                    )}
                </div>
            </Fragment>
        );
    }

    function onRestrictedSelect(
        value: FilterOption,
        groupOptions: FilterOption[]
    ) {
        groupOptions.forEach((option) => {
            const selected = selectedOptions.find(
                ({ value: selectedValue }) => selectedValue === option.value
            );
            if (selected && selected.value !== value.value) {
                onSelect?.(selected);
            }
        });
        onSelect?.(value);
    }

    return (
        <StyledSearchFilterMenuPriceModifierOptions data-testid={dataTestId}>
            <div className="header">
                {hasIncludedAccessories
                    ? renderIncludedAccessoriesHeader()
                    : renderExtraAccessoriesHeader()}
                {selectedCount !== undefined && (
                    <span className="selected-count">
                        {t('{{count}} selected', { count: selectedCount })}
                    </span>
                )}
                {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                {onClear && (
                    <Link
                        size={LinkSize.SMALL}
                        onClick={onClear}
                        className="clear"
                        data-testid={`${dataTestId}__clear-button`}
                    >
                        {t('Clear filter')}
                    </Link>
                )}
            </div>

            {includedAccessories.map((group) => (
                <div
                    key={group.id}
                    className="filter-section"
                    data-testid={`${dataTestId}__${group.id}`}
                >
                    <Heading size={HeadingSize.H5}>{group.name}</Heading>
                    <SearchFilterMenuOptionList
                        onSelect={(option) =>
                            onRestrictedSelect(
                                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example.
                                option,
                                getFilterOptions(group.modifiers)
                            )
                        }
                        displayOptionQuantity={false}
                        autoFocus={false}
                        isLoading={isLoadingData}
                        showScrollFade={false}
                        filter={{
                            ...filter,
                            label: group.name,
                            options: getFilterOptions(group.modifiers),
                        }}
                        selectedOptions={selectedOptionsWithDefaults}
                    />
                </div>
            ))}

            {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
            {hasIncludedAccessories && (
                <div className="header">{renderExtraAccessoriesHeader()}</div>
            )}
            <SearchFilterMenuOptionList
                data-testid={`${dataTestId}__extra-accessories`}
                className="filter-options"
                onSelect={onSelect}
                displayOptionQuantity={false}
                autoFocus={false}
                isLoading={isLoadingData}
                showScrollFade={false}
                filter={{
                    ...filter,
                    label: t('Extra accessories'),
                    options: getFilterOptions(extraAccessories),
                }}
                selectedOptions={selectedOptionsWithDefaults}
            />
        </StyledSearchFilterMenuPriceModifierOptions>
    );
};
